// src/products/questions/components/ContextAwareQuestionForm.js
// Context-aware question form that adapts based on module context
import React, { useState, useEffect } from 'react';
import { X, Send, FileText, Folder, Building, Target, Paperclip, Upload } from 'lucide-react';
import simpleQuestionsApi from '../services/simpleQuestionsApi';
import RichTextEditor from './RichTextEditor';
import attachmentService from '../services/attachmentService';

const ContextAwareQuestionForm = ({ 
  isOpen, 
  onClose, 
  context, 
  onSubmit,
  user 
}) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    priority: false,
    module_type: '',
    module_reference_id: '',
    module_reference_name: '',
    project_id: '',
    company_id: '',
    folder_id: '',
    folder_name: '',
    file_id: '',
    file_name: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);

  const isGeneralContext = (formData.module_type || 'general') === 'general';
  const isDealroomContext = formData.module_type === 'dataroom';
  const hasProjectContext = Boolean(context?.projectId);

  // Update form data when context changes
  useEffect(() => {
    if (context) {
      const moduleType = context.moduleType || 'general';
      setFormData(prev => ({
        ...prev,
        module_type: moduleType,
        // For general questions, clear dataroom-specific fields
        module_reference_id: moduleType === 'general' ? null : (context.moduleReferenceId || ''),
        module_reference_name: moduleType === 'general' ? null : (context.moduleReferenceName || ''),
        project_id: moduleType === 'general' ? null : (context.projectId || ''),
        company_id: moduleType === 'general' ? null : (context.companyId || ''),
        folder_id: context.folderId || '',
        folder_name: context.folderName || '',
        file_id: context.fileId || '',
        file_name: context.fileName || '',
        category: getDefaultCategory(moduleType)
      }));
    } else {
      // Default to general if no context
      setFormData(prev => ({
        ...prev,
        module_type: 'general',
        module_reference_id: null,
        module_reference_name: null,
        project_id: null,
        company_id: null,
        folder_id: '',
        folder_name: '',
        file_id: '',
        file_name: '',
        category: 'General'
      }));
    }
  }, [context]);

  // Load user's dealroom projects for dealroom context
  useEffect(() => {
    const loadProjects = async () => {
      if (formData.module_type === 'dataroom' && !hasProjectContext) {
        setLoadingProjects(true);
        try {
          const { supabase } = await import('../../../config');
          
          // Get current user
          const { data: { user: currentUser } } = await supabase.auth.getUser();
          if (!currentUser) return;

          // Fetch projects where user has access via dataroom_users table
          // This includes both creators and viewers
          const { data, error } = await supabase
            .from('dataroom_users')
            .select(`
              project_id,
              role,
              dataroom_projects (
                id,
                project_name
              )
            `)
            .eq('user_id', currentUser.id)
            .order('dataroom_projects(project_name)');

          if (error) throw error;
          
          // Extract unique projects
          const projectsList = (data || [])
            .map(item => item.dataroom_projects)
            .filter(Boolean)
            .filter((project, index, self) => 
              index === self.findIndex(p => p.id === project.id)
            );
          
          setProjects(projectsList);
        } catch (error) {
          console.error('Error loading projects:', error);
        } finally {
          setLoadingProjects(false);
        }
      }
    };

    loadProjects();
  }, [formData.module_type, hasProjectContext]);

  // Load folders and files when project is selected
  useEffect(() => {
    const loadDataroomItems = async () => {
      if (formData.module_type === 'dataroom' && formData.project_id) {
        setLoadingItems(true);
        try {
          const { supabase } = await import('../../../config');
          const { data, error } = await supabase
            .from('dataroom_items')
            .select('id, name, is_folder, parent_id')
            .eq('project_id', formData.project_id)
            .order('is_folder', { ascending: false })
            .order('name');

          if (error) throw error;

          const folderList = data.filter(item => item.is_folder);
          const fileList = data.filter(item => !item.is_folder);
          
          setFolders(folderList);
          setFiles(fileList);
        } catch (error) {
          console.error('Error loading dataroom items:', error);
        } finally {
          setLoadingItems(false);
        }
      } else {
        setFolders([]);
        setFiles([]);
      }
    };

    loadDataroomItems();
  }, [formData.module_type, formData.project_id]);

  // Get default category based on module type
  const getDefaultCategory = (moduleType) => {
    switch (moduleType) {
      case 'dataroom':
        return 'Dealroom';
      case 'capraise':
        return 'CapRaise';
      case 'equity_manager':
        return 'Equity';
      default:
        return 'General';
    }
  };

  // Get module-specific categories
  const getModuleCategories = (moduleType) => {
    switch (moduleType) {
      case 'dataroom':
        return [
          'Document Access',
          'File Organization', 
          'Permission Issues',
          'Upload Problems',
          'Download Issues',
          'General'
        ];
      case 'capraise':
        return [
          'Investment Terms',
          'Due Diligence',
          'Financial Information',
          'Legal Documents',
          'Timeline Questions',
          'General'
        ];
      case 'equity_manager':
        return [
          'Share Classes',
          'Transactions',
          'Valuation',
          'Compliance',
          'Reporting',
          'General'
        ];
      default:
        // Support questions
        return [
          'Account',
          'Billing',
          'Technical',
          'Feature Request',
          'Bug Report',
          'General'
        ];
    }
  };

  // Get context display info
  const getContextInfo = () => {
    if (!context) return null;

    switch (context.moduleType) {
      case 'dataroom':
        return {
          icon: Folder,
          label: 'Dealroom',
          value: context.moduleReferenceName || 'Project'
        };
      case 'capraise':
        return {
          icon: Target,
          label: 'Capital Raising',
          value: context.moduleReferenceName || 'Round'
        };
      case 'equity_manager':
        return {
          icon: Building,
          label: 'Equity Manager',
          value: context.moduleReferenceName || 'Company'
        };
      default:
        return {
          icon: FileText,
          label: 'General',
          value: 'Support'
        };
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    // For dealroom questions, project_id is required
    if (isDealroomContext && !formData.project_id) {
      newErrors.project_id = 'Please select a dealroom';
    }

    // Category is not required for dealroom questions (context-based)
    if (!isDealroomContext && !formData.category) {
      newErrors.category = 'Category is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleDescriptionChange = (value) => {
    setFormData(prev => ({
      ...prev,
      description: value
    }));

    if (errors.description) {
      setErrors(prev => ({
        ...prev,
        description: ''
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file
      const validation = attachmentService.validateFile(file);
      if (!validation.isValid) {
        setErrors(prev => ({ ...prev, attachment: validation.errors.join(', ') }));
        setAttachmentFile(null);
        e.target.value = null;
      } else {
        setAttachmentFile(file);
        setErrors(prev => ({ ...prev, attachment: '' }));
      }
    }
  };

  const removeAttachment = () => {
    setAttachmentFile(null);
    setErrors(prev => ({ ...prev, attachment: '' }));
    const fileInput = document.getElementById('context-attachment-file');
    if (fileInput) fileInput.value = null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      // Ensure module_type is set correctly
      const moduleType = formData.module_type || 'general';
      
      // Prepare question data - clear dataroom fields for general questions
      const questionData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        priority: formData.priority ? 'high' : 'medium',
        module_type: moduleType,
        user_id: user?.id || 'current_user',
        status: 'open'
      };

      // Only include dataroom-specific fields if not general
      if (moduleType !== 'general') {
        questionData.module_reference_id = formData.module_reference_id || null;
        questionData.module_reference_name = formData.module_reference_name || null;
        questionData.project_id = formData.project_id || null;
        questionData.company_id = formData.company_id || null;
      } else {
        // Explicitly set to null for general questions
        questionData.module_reference_id = null;
        questionData.module_reference_name = null;
        questionData.project_id = null;
        questionData.company_id = null;
      }

      const result = await simpleQuestionsApi.createQuestion(questionData);
      
      if (result.success) {
        // If there's an attachment, upload it
        if (attachmentFile && result.data && result.data.id) {
          setUploadingAttachment(true);
          try {
            const uploadResult = await attachmentService.uploadAttachment(
              result.data.id,
              attachmentFile,
              user?.id || user?.uid || 'anonymous',
              'file'
            );
            
            if (uploadResult.success) {
              console.log('Attachment uploaded successfully:', uploadResult.data);
            } else {
              console.error('Attachment upload failed');
              // Non-blocking - don't prevent form close
            }
          } catch (uploadError) {
            console.error('Error uploading attachment:', uploadError);
            // Non-blocking - don't prevent form close
          } finally {
            setUploadingAttachment(false);
          }
        }
        
        // Call parent onSubmit callback if provided
        if (onSubmit) {
          onSubmit(result.data);
        }
        
        // Reset form and close
        setFormData({
          title: '',
          description: '',
          category: getDefaultCategory(context?.moduleType),
          priority: false,
          module_type: context?.moduleType || '',
          module_reference_id: context?.moduleReferenceId || '',
          module_reference_name: context?.moduleReferenceName || '',
          project_id: context?.projectId || '',
          company_id: context?.companyId || ''
        });
        setErrors({});
        setAttachmentFile(null);
        onClose();
      } else {
        setErrors({ submit: result.message || 'Failed to create question' });
      }
    } catch (error) {
      console.error('Error creating question:', error);
      setErrors({ submit: 'An error occurred while creating the question' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      description: '',
      category: getDefaultCategory(context?.moduleType),
      priority: false,
      module_type: context?.moduleType || '',
      module_reference_id: context?.moduleReferenceId || '',
      module_reference_name: context?.moduleReferenceName || '',
      project_id: context?.projectId || '',
      company_id: context?.companyId || '',
      folder_id: context?.folderId || '',
      folder_name: context?.folderName || '',
      file_id: context?.fileId || '',
      file_name: context?.fileName || ''
    });
    setErrors({});
    setAttachmentFile(null);
    onClose();
  };

  if (!isOpen) return null;

  const contextInfo = getContextInfo();
  const ContextIcon = contextInfo?.icon || FileText;

  // Get detailed context path for display
  const getContextPath = () => {
    if (formData.module_type === 'dataroom') {
      const path = [];
      
      // Project/Dealroom name
      if (formData.module_reference_name) {
        path.push(formData.module_reference_name);
      } else if (context?.moduleReferenceName) {
        path.push(context.moduleReferenceName);
      } else if (formData.project_id) {
        const selectedProject = projects.find(p => p.id === parseInt(formData.project_id));
        if (selectedProject) path.push(selectedProject.project_name);
        else path.push('Dealroom');
      } else {
        path.push('Dealroom');
      }
      
      // Folder
      if (formData.folder_name) path.push(formData.folder_name);
      else if (context?.folderName) path.push(context.folderName);
      
      // File
      if (formData.file_name) path.push(formData.file_name);
      else if (context?.fileName) path.push(context.fileName);
      
      return path.join(' > ');
    }
    
    if (!context) return 'General Support';
    
    switch (context.moduleType) {
      case 'capraise':
        return context.moduleReferenceName || 'Capital Raising Round';
      case 'equity_manager':
        return context.moduleReferenceName || 'Equity Management';
      default:
        return 'General Support';
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900 bg-opacity-75">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-white px-6 pt-4 pb-4 sm:p-6 sm:pb-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <ContextIcon className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl leading-6 font-semibold text-gray-900">
                    Create New Question
                  </h3>
                  {contextInfo && (
                    <p className="text-sm text-gray-500">
                      Context: {getContextPath()}
                    </p>
                  )}
                  {/* Show admin notification for general questions */}
                  {isGeneralContext && (
                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                      <p className="text-xs text-blue-700">
                        <strong>Note:</strong> This question will be sent to site administrators for support.
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Context Display */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">
                  Context / Module
                </label>
                <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-sm text-gray-700">
                  {getContextPath()}
                </div>
              </div>

              {/* Dealroom Selector - only if not already in a dealroom context */}
              {isDealroomContext && !hasProjectContext && (
                <div>
                  <label htmlFor="project_id" className="text-sm font-medium text-gray-700 block mb-1">
                    Dealroom *
                  </label>
                  <select
                    name="project_id"
                    value={formData.project_id}
                    onChange={(e) => {
                      const selectedProject = projects.find(p => p.id === parseInt(e.target.value));
                      setFormData(prev => ({
                        ...prev,
                        project_id: e.target.value,
                        module_reference_id: e.target.value,
                        module_reference_name: selectedProject?.project_name || '',
                        folder_id: '',
                        folder_name: '',
                        file_id: '',
                        file_name: ''
                      }));
                    }}
                    className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                      errors.project_id ? 'border-red-300' : 'border-gray-300'
                    }`}
                    disabled={loadingProjects}
                  >
                    <option value="">Select a dealroom</option>
                    {projects.map(project => (
                      <option key={project.id} value={project.id}>{project.project_name}</option>
                    ))}
                  </select>
                  {loadingProjects && (
                    <p className="mt-1 text-sm text-gray-500">Loading dealrooms...</p>
                  )}
                  {errors.project_id && (
                    <p className="mt-1 text-sm text-red-600">{errors.project_id}</p>
                  )}
                </div>
              )}

              {/* Subject Line */}
              <div>
                <label htmlFor="title" className="text-sm font-medium text-gray-700 block mb-1">
                  Subject Line *
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Brief summary of your question"
                  className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                    errors.title ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                )}
              </div>

              {/* Dealroom Context: Folder/File Selectors - only show if project is selected */}
              {isDealroomContext && formData.project_id && !formData.folder_id && !formData.file_id && (
                <>
                  {/* Folder Selector */}
                  <div>
                    <label htmlFor="folder_id" className="text-sm font-medium text-gray-700 block mb-1">
                      Folder (Optional)
                    </label>
                    <select
                      name="folder_id"
                      value={formData.folder_id}
                      onChange={(e) => {
                        const selectedFolder = folders.find(f => f.id === parseInt(e.target.value));
                        setFormData(prev => ({
                          ...prev,
                          folder_id: e.target.value,
                          folder_name: selectedFolder?.name || ''
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      disabled={loadingItems}
                    >
                      <option value="">Select folder (optional)</option>
                      {folders.map(folder => (
                        <option key={folder.id} value={folder.id}>{folder.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* File Selector */}
                  <div>
                    <label htmlFor="file_id" className="text-sm font-medium text-gray-700 block mb-1">
                      File (Optional)
                    </label>
                    <select
                      name="file_id"
                      value={formData.file_id}
                      onChange={(e) => {
                        const selectedFile = files.find(f => f.id === parseInt(e.target.value));
                        setFormData(prev => ({
                          ...prev,
                          file_id: e.target.value,
                          file_name: selectedFile?.name || ''
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      disabled={loadingItems}
                    >
                      <option value="">Select file (optional)</option>
                      {files.map(file => (
                        <option key={file.id} value={file.id}>{file.name}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {/* Category - only for non-dealroom questions */}
              {!isDealroomContext && (
                <div>
                  <label htmlFor="category" className="text-sm font-medium text-gray-700 block mb-1">
                    {isGeneralContext ? 'Support Category *' : 'Category *'}
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                      errors.category ? 'border-red-300' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select a category</option>
                    {getModuleCategories(formData.module_type).map(category => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                  {errors.category && (
                    <p className="mt-1 text-sm text-red-600">{errors.category}</p>
                  )}
                </div>
              )}

              {/* Content with Rich Text Editor */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">
                  Content *
                </label>
                <RichTextEditor
                  value={formData.description}
                  onChange={handleDescriptionChange}
                  placeholder="Enter your question content..."
                  disabled={loading}
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                )}
              </div>

              {/* Priority */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="priority"
                  name="priority"
                  checked={formData.priority}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="priority" className="ml-2 block text-sm text-gray-700">
                  High Priority
                </label>
              </div>

              {/* Attachment */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Attachment (Optional)
                </label>
                {!attachmentFile ? (
                  <div className="flex items-center">
                    <label 
                      htmlFor="context-attachment-file" 
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Choose File
                    </label>
                    <input
                      type="file"
                      id="context-attachment-file"
                      onChange={handleFileChange}
                      disabled={loading}
                      className="hidden"
                    />
                    <span className="ml-3 text-xs text-gray-500">
                      Max 10MB • PDF, Images, Documents
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="flex items-center min-w-0">
                      <Paperclip className="h-4 w-4 text-blue-600 mr-2 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-blue-900 truncate">
                          {attachmentFile.name}
                        </p>
                        <p className="text-xs text-blue-700">
                          {attachmentService.formatFileSize(attachmentFile.size)}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={removeAttachment}
                      disabled={loading}
                      className="ml-3 text-blue-600 hover:text-blue-800 flex-shrink-0"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}
                {errors.attachment && (
                  <p className="mt-1 text-sm text-red-600">{errors.attachment}</p>
                )}
              </div>

              {/* Submit Error */}
              {errors.submit && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{errors.submit}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || uploadingAttachment}
                  className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center space-x-2"
                >
                  {uploadingAttachment ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Uploading file...</span>
                    </>
                  ) : loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Creating...</span>
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      <span>Ask Question</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContextAwareQuestionForm;
